import React, { FC, useRef, useEffect, useContext } from "react";
import { Field } from "react-final-form";
import Select from "react-select";
import { ThemeContext } from "styled-components";
import { getThemedSelectStyles } from "../../../helpers/field";
import { DropdownIndicator } from "../../dropdown/DropdownIndicator";
import { FieldProps } from "../../../types/field";
import { ReactSelectDropdownOption } from "../../../types/dropdown";
import { StyledField, StyledLabel, StyledError } from "../base.styled";

interface SelectProps extends FieldProps {
  options: ReactSelectDropdownOption[];
  defaultValue?: ReactSelectDropdownOption;
  autocompleteValue?: ReactSelectDropdownOption;
  browserAutofillOffValue?: string;
  placeholder?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OnChangeType = (event: any) => void;

export const FieldSelect: FC<SelectProps> = ({
  name,
  autocompleteValue,
  browserAutofillOffValue,
  placeholder,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const inputOnChange = useRef<OnChangeType | null>(null);

  /* setting the Input under components causes a tabbing bug, this is a hacky
     workaround until react-select accepts an autoComplete value or fixes the
     tabbing bug. So, DON'T DO THIS:

    components={{
      Input: ({ ...rest }) => (
        <components.Input
          {...rest}
          autoComplete={browserAutofillOffValue}
        />
      ),
  */
  useEffect(() => {
    if (browserAutofillOffValue) {
      const fieldSelectInputs = document.querySelectorAll(
        `.react-select input[autocomplete="off"]`
      );

      if (fieldSelectInputs) {
        fieldSelectInputs.forEach((input) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          input.autocomplete = browserAutofillOffValue;
        });
      }
    }
  }, [browserAutofillOffValue]);

  // handle an incoming change from the AutocompleteInput
  useEffect(() => {
    if (inputOnChange.current && autocompleteValue !== undefined) {
      inputOnChange.current(autocompleteValue);
    }
  }, [autocompleteValue, inputOnChange]);

  return (
    <Field name={name} {...props}>
      {({ input, meta, ...rest }) => {
        if (!inputOnChange.current) {
          inputOnChange.current = input.onChange;
        }

        const valid = !!meta.touched && !meta.error;
        const invalid = !!meta.touched && !!meta.error;
        const focused = !!meta.active;

        return (
          <StyledField className="react-select" data-testid={name}>
            <StyledLabel>
              {props.label && <div>{props.label}</div>}
              <Select
                {...input}
                {...rest}
                components={{
                  DropdownIndicator,
                }}
                defaultValue={props.defaultValue}
                placeholder={placeholder || <>&mdash;</>}
                styles={getThemedSelectStyles(theme, valid, invalid, focused)}
                // https://github.com/JedWatson/react-select/issues/2629
                instanceId={name}
              />
            </StyledLabel>
            {invalid && <StyledError>{meta.error}</StyledError>}
          </StyledField>
        );
      }}
    </Field>
  );
};
